import Alpine from 'alpinejs'

export default (config = {}) => ({
  startRaw: config.start || null,
  endRaw: config.end || null,
  labels: {
    pre: config.preLabel || 'Early voting starts in',
    during: config.duringLabel || 'Time left to early vote',
    post: config.postLabel || 'Voting has started!',
  },
  intervalId: null,
  phase: 'missing',
  startDate: null,
  endDate: null,
  units: [
    { key: 'months', label: 'Months', value: '00' },
    { key: 'days', label: 'Days', value: '00' },
    { key: 'hours', label: 'Hours', value: '00' },
    { key: 'seconds', label: 'Seconds', value: '00' },
  ],

  init() {
    this.startDate = this.parseDate(this.startRaw)
    this.endDate = this.parseDate(this.endRaw)

    if (!this.hasValidDates) {
      return
    }

    this.updatePhase()

    if (this.phase === 'ended') {
      // Set all units to zero when early voting has ended
      this.units = this.units.map(unit => ({
        ...unit,
        value: '00',
      }))
    } else {
      this.updateUnits()
      this.startTicker()
    }
  },

  destroyed() {
    this.stopTicker()
  },

  parseDate(value) {
    if (!value) return null
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  },

  get hasValidDates() {
    return (
      this.startDate instanceof Date &&
      this.endDate instanceof Date &&
      this.startDate < this.endDate
    )
  },

  get showCountdown() {
    return this.hasValidDates
  },

  get statusText() {
    if (this.phase === 'upcoming') return this.labels.pre
    if (this.phase === 'active') return this.labels.during
    return this.labels.post
  },

  updatePhase() {
    const now = new Date()

    if (now < this.startDate) {
      this.phase = 'upcoming'
    } else if (now >= this.startDate && now <= this.endDate) {
      this.phase = 'active'
    } else {
      this.phase = 'ended'
      this.stopTicker()
    }
  },

  startTicker() {
    this.stopTicker()

    this.intervalId = setInterval(() => {
      this.updatePhase()

      if (this.phase === 'ended') return

      this.updateUnits()
    }, 1000)
  },

  stopTicker() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  },

  updateUnits() {
    const target = this.phase === 'upcoming' ? this.startDate : this.endDate
    const now = new Date()
    const breakdown = this.calculateBreakdown(now, target)

    this.units = this.units.map(unit => ({
      ...unit,
      value: this.padValue(breakdown[unit.key]),
    }))
  },

  calculateBreakdown(from, to) {
    if (to <= from) {
      return { months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 }
    }

    let cursor = new Date(from.getTime())
    let months = 0

    while (true) {
      const next = new Date(cursor.getTime())
      next.setMonth(next.getMonth() + 1)

      if (next <= to) {
        months += 1
        cursor = next
      } else {
        break
      }
    }

    let diffMs = to.getTime() - cursor.getTime()
    const dayMs = 86_400_000
    const hourMs = 3_600_000
    const minuteMs = 60_000
    const secondMs = 1_000

    const days = Math.floor(diffMs / dayMs)
    diffMs -= days * dayMs

    const hours = Math.floor(diffMs / hourMs)
    diffMs -= hours * hourMs

    const minutes = Math.floor(diffMs / minuteMs)
    diffMs -= minutes * minuteMs

    const seconds = Math.floor(diffMs / secondMs)

    return { months, days, hours, minutes, seconds }
  },

  padValue(value) {
    return String(Math.max(0, value ?? 0)).padStart(2, '0')
  },
})
